import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Recipient, RecipientData } from './walletRecipientApi.types';

const queryKeys = {
  all: () => ['wallet-recipients'] as const,
  recipients: (merchantId?: string, currency?: string) =>
    [...queryKeys.all(), 'recipients', merchantId, currency] as const,
  recipient: (merchantId: string, contactId: string) =>
    [...queryKeys.all(), 'recipient', merchantId, contactId] as const,
};

export const useGetRecipients = ({
  merchantId,
  currency,
}: {
  merchantId?: string;
  currency: string;
}) => {
  return useQuery<Recipient[], ErrorResponse>({
    enabled: !!merchantId,
    queryKey: queryKeys.recipients(merchantId, currency),
    queryFn: async () => {
      const response = await fetchAPI<{ recipients: Recipient[] }>(
        `v1/merchants/${merchantId}/wallet-recipients?currency=${currency}`
      );
      return response.recipients;
    },
  });
};

export const useGetRecipient = ({
  merchantId,
  contactId,
  enabled = true,
}: {
  merchantId: string;
  contactId: string;
  enabled?: boolean;
}) => {
  return useQuery<Recipient, ErrorResponse>({
    enabled: enabled && !!merchantId && !!contactId,
    queryKey: queryKeys.recipient(merchantId, contactId),
    queryFn: async () => {
      return await fetchAPI<Recipient>(
        `v1/merchants/${merchantId}/wallet-recipients/${contactId}`
      );
    },
  });
};

export const useAddRecipient = (merchantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<Recipient, ErrorResponse, RecipientData>({
    mutationFn: async ({ address, label, currency }: RecipientData) => {
      return await fetchAPI<Recipient>(
        `v1/merchants/${merchantId}/wallet-recipients`,
        {
          method: 'POST',
          body: {
            address,
            label,
            currency,
          },
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.all(),
      });
    },
  });
};

export const useApproveRecipient = (merchantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, ErrorResponse, { contactId: string }>({
    mutationFn: async ({ contactId }) => {
      return await fetchAPI(
        `v1/merchants/${merchantId}/wallet-recipients/${contactId}`,
        {
          method: 'PATCH',
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.all(),
      });
    },
  });
};

export const useRemoveRecipient = (merchantId: string) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, ErrorResponse, { contactId: string }>({
    mutationFn: async ({ contactId }) => {
      return await fetchAPI(
        `v1/merchants/${merchantId}/wallet-recipients/${contactId}`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.all(),
      });
    },
  });
};
